/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Open Source Version is distributed with Sencha Ext JS pursuant to a FLOSS Exception agreed upon
 * between Sonatype, Inc. and Sencha Inc. Sencha Ext JS is licensed under GPL v3 and cannot be redistributed as part of a
 * closed source work.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

/**
 * The endpoints the Nexus One state machine reads, the same ones the ExtJS State and Permissions controllers use:
 * the rapture_State_get poll and the rapture_Security.getPermissions RPC.
 */

const CSRF_COOKIE_NAME = 'NX-ANTI-CSRF-TOKEN';

/** Error thrown for a failed request; carries the HTTP status (undefined on a network error). */
export class StateRequestError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'StateRequestError';
    this.status = status;
  }
}

function servicePath(path) {
  const relativePath = window.NX?.app?.relativePath ?? '.';
  return `${relativePath}/service/${path}`;
}

function getCookie(name) {
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

// Same as NX.app.Application: the token is any value, as long as the cookie and the header match
function csrfToken() {
  let token = getCookie(CSRF_COOKIE_NAME);
  if (!token) {
    token = Math.random().toString();
    document.cookie = `${CSRF_COOKIE_NAME}=${encodeURIComponent(token)}; path=/`;
  }
  return token;
}

function headers(extra = {}) {
  return {
    Accept: 'application/json',
    'X-Nexus-UI': 'true',
    [CSRF_COOKIE_NAME]: csrfToken(),
    ...extra
  };
}

async function request(url, options) {
  let response;
  try {
    response = await fetch(url, {credentials: 'include', ...options});
  }
  catch (error) {
    throw new StateRequestError(error?.message ?? 'Network error');
  }
  if (!response.ok) {
    throw new StateRequestError(`Request failed with status ${response.status}`, response.status);
  }
  return response.json();
}

/**
 * Polls rapture_State_get. The server only returns values whose hash differs from the one sent, and returns null for
 * a key it no longer contributes.
 *
 * The hashes go in a form-encoded POST body, as the ExtJS polling provider sends them: there is one per state key,
 * and as a query string they exceed the server's URI limit (414).
 * @param {Object<string, string>} hashes state key to the hash of the value held
 * @returns {Promise<Object<string, {hash: string, value: *}|null>>} the changed entries
 */
export async function fetchStateValues(hashes) {
  const result = await request(servicePath('extdirect/poll/rapture_State_get'), {
    method: 'POST',
    headers: headers({'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'}),
    body: new URLSearchParams(hashes).toString()
  });
  const data = result?.data?.data;
  if (!data || typeof data !== 'object') {
    throw new StateRequestError(result?.message ?? 'Invalid state response');
  }
  return data;
}

/**
 * Deletes the session, as NX.controller.User.signOut() does.
 * @returns {Promise<string|null>} the Location header to follow (SSO logout), or null
 */
export async function deleteSession() {
  const response = await fetch(servicePath('rapture/session'), {
    method: 'DELETE',
    credentials: 'include',
    headers: headers()
  });
  if (!response.ok) {
    throw new StateRequestError(`Request failed with status ${response.status}`, response.status);
  }
  return response.headers.get('Location');
}

/**
 * Calls rapture_Security.getPermissions.
 * @returns {Promise<Set<string>>} the lower-cased ids of the permissions the subject holds
 */
export async function fetchPermissions() {
  const result = await request(servicePath('extdirect'), {
    method: 'POST',
    headers: headers({'Content-Type': 'application/json'}),
    body: JSON.stringify({
      action: 'rapture_Security',
      method: 'getPermissions',
      data: null,
      type: 'rpc',
      tid: Date.now()
    })
  });
  if (result?.result?.success !== true) {
    throw new StateRequestError(result?.result?.message ?? 'Invalid permissions response');
  }
  // A subject that is neither signed in nor anonymous (anonymous access disabled) gets data: null, which ExtJS's
  // Permission store loads as no permissions
  const data = result.result.data ?? [];
  if (!Array.isArray(data)) {
    throw new StateRequestError(result?.result?.message ?? 'Invalid permissions response');
  }
  // rapture_Security.getPermissions lists only the permissions the subject holds, by id: PermissionXO has no
  // `permitted` field (ExtJS gets `permitted: true` from the NX.model.Permission field default). An explicit
  // `permitted: false` would be ignored, but sending one needs a server change that ExtJS's check would need to follow.
  // Lower case, as the permission check lower cases the permission it is asked about.
  return new Set(data.map(({id}) => id.toLowerCase()));
}
